/**
 * @file Shared linked-parent field and section primitives.
 */

import {
  sanitizeHTML,
} from "../utils/contentUtils.js";
import {
  FIELD_WRAPPER_CLASSES,
  NATIVE_INPUT_CLASSES,
  getFieldLabelHtml,
} from "../forms/nativeFieldStyles.js";

export const LINKED_PARENT_ACTIONS = Object.freeze({
  LINK: "link",
  OPEN: "open",
  UNLINK: "unlink",
});

const ICON_BUTTON_CLASSES = [
  "inline-flex",
  "h-8",
  "w-8",
  "flex-shrink-0",
  "items-center",
  "justify-center",
  "rounded-md",
  "text-gray-500",
  "transition-colors",
  "hover:bg-[var(--dfc-brand-primary)]",
  "hover:text-[var(--dfc-brand-primary-contrast)]",
  "focus:outline-none",
  "focus:ring-2",
  "focus:ring-[var(--dfc-brand-primary-ring)]",
  "disabled:pointer-events-none",
  "disabled:opacity-40",
].join(" ");

const DANGER_ICON_BUTTON_CLASSES = [
  "inline-flex",
  "h-8",
  "w-8",
  "flex-shrink-0",
  "items-center",
  "justify-center",
  "rounded-md",
  "text-gray-500",
  "transition-colors",
  "hover:bg-red-50",
  "hover:text-red-700",
  "focus:outline-none",
  "focus:ring-2",
  "focus:ring-red-200",
  "disabled:pointer-events-none",
  "disabled:opacity-40",
].join(" ");

function attrValue(value = "") {
  return String(value ?? "").replace(/"/g, "&quot;");
}

function renderMetaHtml(meta = []) {
  const values = (Array.isArray(meta) ? meta : [])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
  if (values.length === 0) {return "";}

  return `
    <div class="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
      ${values.map((value) => `<span>${sanitizeHTML(value)}</span>`).join("")}
    </div>
  `;
}

function renderActionButton({
  action,
  disabled = false,
  fieldId,
  icon,
  label,
  targetId = "",
  targetType = "",
  variant = "default",
}) {
  const className = variant === "danger" ?
    DANGER_ICON_BUTTON_CLASSES :
    ICON_BUTTON_CLASSES;
  return `
    <button
      type="button"
      class="${className}"
      data-linked-parent-action="${sanitizeHTML(action)}"
      data-linked-parent-field="${sanitizeHTML(fieldId)}"
      data-linked-parent-id="${sanitizeHTML(targetId)}"
      data-linked-parent-type="${sanitizeHTML(targetType)}"
      title="${sanitizeHTML(label)}"
      aria-label="${sanitizeHTML(label)}"
      ${disabled ? "disabled" : ""}
    >
      <span class="material-symbols-outlined pointer-events-none text-[18px] leading-none">${sanitizeHTML(icon)}</span>
    </button>
  `;
}

/**
 * Renders an editable display field with adjacent linked-parent actions.
 * @param {object} params Field render parameters.
 * @param {string} params.fieldId Stable field/action id.
 * @param {string} params.label Field label.
 * @param {string} params.textName Editable display-name field name.
 * @param {string} [params.textValue] Editable display-name value.
 * @param {string} params.linkIdName Linked parent id field name.
 * @param {string | null} [params.linkIdValue] Linked parent id value.
 * @param {string} [params.targetType] Linked parent target type.
 * @param {string} [params.targetLabel="parent"] User-facing target label.
 * @param {string} [params.placeholder] Text input placeholder.
 * @param {Array<string>} [params.meta] Small metadata values for linked item.
 * @param {boolean} [params.required=false] Whether text input is required.
 * @param {boolean} [params.canOpen=true] Whether open action is enabled.
 * @param {boolean} [params.canLink=true] Whether link/search action is enabled.
 * @param {boolean} [params.canUnlink=true] Whether unlink action is enabled.
 * @param {boolean} [params.fieldClickOpensLink=false] Whether clicking the display input opens link/search.
 * @param {boolean} [params.showLinkAction=true] Whether to render a separate link/change action button.
 * @returns {string} Linked parent field HTML.
 */
export function renderLinkedParentField({
  fieldId,
  label,
  textName,
  textValue = "",
  linkIdName,
  linkIdValue = "",
  targetType = "",
  targetLabel = "parent",
  placeholder = "",
  meta = [],
  required = false,
  canOpen = true,
  canLink = true,
  canUnlink = true,
  fieldClickOpensLink = false,
  showLinkAction = true,
}) {
  const safeFieldId = fieldId || textName || linkIdName || "linked-parent";
  const linkedId = String(linkIdValue || "");
  const hasLink = Boolean(linkedId);
  const displayTarget = targetLabel || "parent";
  const fieldIsClickable = Boolean(fieldClickOpensLink && canLink);
  const inputClasses = [
    NATIVE_INPUT_CLASSES,
    fieldIsClickable ? "cursor-pointer" : "",
    fieldIsClickable || hasLink ? "bg-gray-50 text-gray-700" : "",
    hasLink ? "pr-2" : "",
  ].filter(Boolean).join(" ");
  const triggerAttrs = fieldIsClickable ? `
          readonly
          role="button"
          data-linked-parent-trigger="${sanitizeHTML(LINKED_PARENT_ACTIONS.LINK)}"
          data-linked-parent-field="${sanitizeHTML(safeFieldId)}"
          data-linked-parent-id="${sanitizeHTML(linkedId)}"
          data-linked-parent-type="${sanitizeHTML(targetType)}"
          title="${sanitizeHTML(hasLink ? `Change linked ${displayTarget}` : `Link ${displayTarget}`)}"
        ` : "";

  const actionHtml = [
    showLinkAction ? renderActionButton({
      action: LINKED_PARENT_ACTIONS.LINK,
      disabled: !canLink,
      fieldId: safeFieldId,
      icon: hasLink ? "sync_alt" : "link",
      label: hasLink ? `Change linked ${displayTarget}` : `Link ${displayTarget}`,
      targetId: linkedId,
      targetType,
    }) : "",
    hasLink && canOpen ? renderActionButton({
      action: LINKED_PARENT_ACTIONS.OPEN,
      disabled: false,
      fieldId: safeFieldId,
      icon: "open_in_new",
      label: `Open linked ${displayTarget}`,
      targetId: linkedId,
      targetType,
    }) : "",
    hasLink ? renderActionButton({
      action: LINKED_PARENT_ACTIONS.UNLINK,
      disabled: !canUnlink,
      fieldId: safeFieldId,
      icon: "link_off",
      label: `Unlink ${displayTarget}`,
      targetId: linkedId,
      targetType,
      variant: "danger",
    }) : "",
  ].join("");

  return `
    <div class="${FIELD_WRAPPER_CLASSES}" data-linked-parent-field="${sanitizeHTML(safeFieldId)}">
      ${getFieldLabelHtml(label, required)}
      <input type="hidden" name="${sanitizeHTML(linkIdName)}" value="${attrValue(linkedId)}">
      <div class="flex min-w-0 items-center gap-2">
        <input
          type="text"
          name="${sanitizeHTML(textName)}"
          value="${attrValue(textValue)}"
          class="${inputClasses}"
          placeholder="${attrValue(placeholder)}"
          autocomplete="off"
          ${required ? "required" : ""}
          ${triggerAttrs}
        >
        <div class="flex flex-shrink-0 items-center gap-1">${actionHtml}</div>
      </div>
      ${renderMetaHtml(meta)}
    </div>
  `;
}

/**
 * Attaches linked-parent field action listeners.
 * @param {HTMLElement} container Root container.
 * @param {object} handlers Action handlers keyed by action id.
 * @returns {void}
 */
export function attachLinkedParentFieldListeners(container, handlers = {}) {
  const invokeHandler = (event, element, actionAttribute = "linkedParentAction") => {
    event.preventDefault();
    event.stopPropagation();
    const action = element.dataset[actionAttribute] || "";
    const handler = handlers[action];
    if (typeof handler !== "function") {return;}

    handler({
      action,
      button: element,
      fieldId: element.dataset.linkedParentField || "",
      targetId: element.dataset.linkedParentId || "",
      targetType: element.dataset.linkedParentType || "",
    });
  };

  container?.querySelectorAll("[data-linked-parent-action]").forEach((button) => {
    button.addEventListener("click", (event) =>
      invokeHandler(event, button, "linkedParentAction"),
    );
  });

  container?.querySelectorAll("[data-linked-parent-trigger]").forEach((trigger) => {
    trigger.addEventListener("click", (event) =>
      invokeHandler(event, trigger, "linkedParentTrigger"),
    );
    trigger.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") {return;}
      invokeHandler(event, trigger, "linkedParentTrigger");
    });
  });
}

/**
 * Renders a generic linked-item section shell for detail views.
 * @param {object} params Section render parameters.
 * @param {string} params.sectionId Stable section id.
 * @param {string} params.title Section title.
 * @param {Array<object>} [params.items=[]] Linked items.
 * @param {Function} params.renderItem Item renderer.
 * @param {string} [params.emptyText] Empty-state text.
 * @param {string} [params.actionHtml] Header action HTML.
 * @returns {string} Linked section HTML.
 */
export function renderLinkedParentSection({
  sectionId,
  title,
  items = [],
  renderItem,
  emptyText = "No linked items.",
  actionHtml = "",
}) {
  const safeSectionId = sectionId || "linked-parent-section";
  const rows = Array.isArray(items) ? items : [];
  const bodyHtml = rows.length > 0 && typeof renderItem === "function" ?
    rows.map((item) => renderItem(item)).join("") :
    `<p class="text-sm text-gray-500 dark:text-gray-400">${sanitizeHTML(emptyText)}</p>`;

  return `
    <section
      class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6 mt-6"
      data-linked-parent-section="${sanitizeHTML(safeSectionId)}"
    >
      <div class="mb-4 flex items-center justify-between gap-3">
        <h2 class="text-xl font-semibold dark:text-gray-100">${sanitizeHTML(title)}</h2>
        ${actionHtml}
      </div>
      <div class="space-y-3">${bodyHtml}</div>
    </section>
  `;
}

/**
 * Renders a compact linked-section row.
 * @param {object} params Row render parameters.
 * @param {string} params.id Linked item id.
 * @param {string} params.title Primary text.
 * @param {string} [params.subtitle] Secondary text.
 * @param {string} [params.trailingHtml] Optional trailing HTML.
 * @returns {string} Linked-section row HTML.
 */
export function renderLinkedParentSectionRow({
  id,
  title,
  subtitle = "",
  trailingHtml = "",
}) {
  return `
    <button
      type="button"
      class="linked-parent-section-row flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left text-sm shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--dfc-brand-primary-ring)] dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
      data-linked-parent-section-item-id="${sanitizeHTML(id)}"
    >
      <span class="min-w-0">
        <span class="block truncate font-medium text-gray-900 dark:text-gray-100">${sanitizeHTML(title || "Untitled")}</span>
        ${subtitle ? `<span class="mt-1 block truncate text-xs text-gray-500 dark:text-gray-400">${sanitizeHTML(subtitle)}</span>` : ""}
      </span>
      ${trailingHtml ? `<span class="flex-shrink-0">${trailingHtml}</span>` : ""}
    </button>
  `;
}

/**
 * Attaches linked-section row click behavior.
 * @param {HTMLElement} container Root container.
 * @param {object} handlers Section handlers.
 * @param {Function} [handlers.onOpenItem] Linked item open callback.
 * @returns {void}
 */
export function attachLinkedParentSectionListeners(container, handlers = {}) {
  container?.querySelectorAll("[data-linked-parent-section-item-id]")
    .forEach((row) => {
      row.addEventListener("click", (event) => {
        event.preventDefault();
        const itemId = row.dataset.linkedParentSectionItemId || "";
        if (!itemId || typeof handlers.onOpenItem !== "function") {return;}
        handlers.onOpenItem({ itemId, row });
      });
    });
}
